import type { Practitioner, PractitionerRole } from '@medplum/fhirtypes';
import { useMemo } from 'react';
import { Link } from 'react-router-dom';

import { AvatarWithInitials } from '@eluve/blocks';
import { Stack, Tag, Text } from '@eluve/components';
import { LoadMoreRow, useFhirSearchPaged } from '@eluve/fhir-data';
import { FhirTable, type FhirTableColumn } from '@eluve/fhir-renderer';
import {
  FHIR_USER_ID_SYSTEM,
  type PractitionerRoleCode,
  practitionerRoleCodeOf,
} from '@eluve/fhir-utils';
import { useAssignedTenantIdFromParams } from '@eluve/session-helpers';

const ROLE_LABELS: Record<PractitionerRoleCode, string> = {
  clinician: 'Clinician',
  'front-desk': 'Front desk',
};

function practitionerName(practitioner: Practitioner): string {
  const name = practitioner.name?.[0];
  return (
    name?.text ??
    [name?.given?.join(' '), name?.family].filter(Boolean).join(' ')
  );
}

function emailOf(practitioner: Practitioner): string | undefined {
  return practitioner.telecom?.find((entry) => entry.system === 'email')?.value;
}

function eluveUserId(practitioner: Practitioner): string | undefined {
  return practitioner.identifier?.find(
    (identifier) => identifier.system === FHIR_USER_ID_SYSTEM,
  )?.value;
}

/** `Practitioner/<id>` → `<id>`; tolerates absolute URLs. */
function referencedPractitionerId(role: PractitionerRole): string | undefined {
  const parts = role.practitioner?.reference?.split('/');
  return parts && parts.length >= 2 ? parts[parts.length - 1] : undefined;
}

/**
 * The members roster as FHIR sees it: every mirrored Practitioner (keyed by
 * the eluve user-id identifier) joined against the project's
 * PractitionerRoles for the practice role. Read-only — configuration happens
 * on the member detail page this table links into.
 */
export const MembersFhirView: React.FC = () => {
  const tenantId = useAssignedTenantIdFromParams();

  // System-only identifier token: every Practitioner carrying an eluve user
  // id, whatever the value — i.e. the mirrored ones, not ad-hoc FHIR-born
  // practitioners.
  const practitioners = useFhirSearchPaged('Practitioner', {
    identifier: `${FHIR_USER_ID_SYSTEM}|`,
    _count: '50',
  });

  const practitionerRefs = useMemo(
    () =>
      practitioners.resources
        .map((practitioner) => practitioner.id)
        .filter((id): id is string => Boolean(id))
        .map((id) => `Practitioner/${id}`),
    [practitioners.resources],
  );

  // Scoped to the practitioners actually on screen rather than the whole
  // project: the join then can't go stale against a roster page, and the role
  // fetch grows only as the roster does.
  const roles = useFhirSearchPaged(
    'PractitionerRole',
    practitionerRefs.length
      ? {
          practitioner: practitionerRefs.join(','),
          _count: String(practitionerRefs.length * 20),
        }
      : null,
  );
  // hasMore here means roles were cut off mid-join, so the column can't tell
  // "no role" from "not fetched" — same failure mode as an errored search.
  const rolesUnavailable = Boolean(roles.error) || roles.hasMore;

  const rolesByPractitioner = useMemo(() => {
    const map = new Map<string, PractitionerRole[]>();
    for (const role of roles.resources) {
      const id = referencedPractitionerId(role);
      if (!id) continue;
      map.set(id, [...(map.get(id) ?? []), role]);
    }
    return map;
  }, [roles.resources]);

  const columns = useMemo<FhirTableColumn[]>(
    () => [
      {
        header: 'Name',
        cell: (resource) => {
          const practitioner = resource as Practitioner;
          const userId = eluveUserId(practitioner);
          const name = practitionerName(practitioner) || 'Practitioner';
          const label = (
            <Stack
              direction="horizontal"
              gap={2}
              align="center"
              fullWidth={false}
            >
              <AvatarWithInitials name={name} id={userId ?? resource.id} />
              <Text inline size="sm" weight="semibold">
                {name}
              </Text>
            </Stack>
          );
          if (!userId) return label;
          return (
            <Link
              to={`/organizations/${tenantId}/settings/members/${userId}`}
              className="hover:underline"
            >
              {label}
            </Link>
          );
        },
      },
      {
        header: 'Email',
        cell: (resource) => (
          <Text inline size="sm" color="tertiary">
            {emailOf(resource as Practitioner)}
          </Text>
        ),
      },
      {
        header: 'Practice role',
        cell: (resource) => {
          // Neither an in-flight nor a failed roles search may masquerade as
          // "Not set" — the roles refetch every time the roster pages in more
          // practitioners, so this renders on every Load more too.
          if (roles.loading) {
            return (
              <Text inline size="xs" color="tertiary">
                …
              </Text>
            );
          }
          if (rolesUnavailable) {
            return (
              <Text inline size="xs" color="tertiary">
                Unavailable
              </Text>
            );
          }
          const practitioner = resource as Practitioner;
          const code = practitionerRoleCodeOf(
            practitioner.id
              ? (rolesByPractitioner.get(practitioner.id) ?? [])
              : [],
          );
          if (!code) {
            return (
              <Text inline size="xs" color="tertiary">
                Not set
              </Text>
            );
          }
          return (
            <Tag
              pill
              subtle
              size="xs"
              color={code === 'clinician' ? 'teal' : 'purple'}
              label={ROLE_LABELS[code]}
            />
          );
        },
      },
      {
        header: 'Status',
        cell: (resource) =>
          (resource as Practitioner).active === false ? (
            <Tag pill subtle size="xs" color="gray" label="Inactive" />
          ) : (
            <Tag pill subtle size="xs" color="positive" label="Active" />
          ),
      },
      {
        header: 'FHIR ID',
        cell: (resource) => (
          <Text inline size="xs" color="tertiary" className="font-mono">
            Practitioner/{resource.id}
          </Text>
        ),
      },
    ],
    [tenantId, rolesByPractitioner, rolesUnavailable, roles.loading],
  );

  if (!practitioners.enabled) {
    return (
      <Text size="sm" color="tertiary">
        FHIR session unavailable.
      </Text>
    );
  }

  if (practitioners.loading) {
    return (
      <Text size="sm" color="tertiary">
        Loading FHIR resources…
      </Text>
    );
  }

  if (practitioners.error) {
    return (
      <Text size="sm" color="tertiary">
        Couldn't load the FHIR resources.
      </Text>
    );
  }

  return (
    <Stack gap={0}>
      <FhirTable
        resources={practitioners.resources}
        columns={columns}
        className="grid items-center gap-x-6 gap-y-3 rounded-md border border-border-primary p-4 text-sm"
        empty={
          <Text size="sm" color="tertiary">
            No mirrored practitioners yet.
          </Text>
        }
      />
      <LoadMoreRow paging={practitioners} noun="members" />
    </Stack>
  );
};
